// components/common/InvoiceSelector.tsx
import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    Typography,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    TextField,
    InputAdornment,
    Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { request } from '../../../../../utils/request';
import { InvoiceOption } from '../../../types';

export interface InvoiceSelectorProps {
    partyId: string | undefined;
    vendorId?: string | undefined;
    gstType?: 'GST' | 'NON-GST';
    onSelectionChange: (selectedInvoices: InvoiceOption[], totalSelectedAmount: number) => void;
    selectedInvoiceIds?: string[];
    multiple?: boolean;
    showBalanceOnly?: boolean;
    compact?: boolean;
    isPurchase?: boolean;
    title?: string;
}

const InvoiceSelector: React.FC<InvoiceSelectorProps> = ({
    partyId,
    vendorId,
    gstType = 'GST',
    onSelectionChange,
    selectedInvoiceIds = [],
    multiple = true,
    showBalanceOnly = true,
    compact = false,
    isPurchase = false,
    title = 'Select Invoices to Settle'
}) => {
    const [loading, setLoading] = useState(false);
    const [invoices, setInvoices] = useState<InvoiceOption[]>([]);
    const [filteredInvoices, setFilteredInvoices] = useState<InvoiceOption[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    // Fetch invoices using partyId directly
    useEffect(() => {
        const fetchInvoices = async () => {
            if (!partyId && !vendorId) {
                setInvoices([]);
                setFilteredInvoices([]);
                return;
            }

            setLoading(true);
            try {
                const url = isPurchase ? 'purchase/get-all-purchase-invoices' : 'invoice/get-all-sales-invoices';
                const res = await request({
                    url: url,
                    method: 'get',
                    params: {
                        partyId: partyId,
                        vendorId: vendorId,
                        gstType: gstType,
                        limit: 100
                    }
                });

                if (res.success && res.data) {
                    let filteredData = res.data;
                    if (showBalanceOnly) {
                        filteredData = res.data.filter((inv: InvoiceOption) =>
                            (inv.balanceAmount || 0) > 0
                        );
                    }

                    setInvoices(filteredData);
                    setFilteredInvoices(filteredData);
                } else {
                    setInvoices([]);
                    setFilteredInvoices([]);
                }
            } catch (error) {
                console.error('Error fetching invoices:', error);
                setInvoices([]);
                setFilteredInvoices([]);
            } finally {
                setLoading(false);
            }
        };

        fetchInvoices();
    }, [partyId, vendorId, gstType, showBalanceOnly, isPurchase]); // Only depend on partyId, gstType, and showBalanceOnly

    // Handle search
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredInvoices(invoices);
            return;
        }

        const term = searchTerm.toLowerCase();
        const filtered = invoices.filter(inv =>
            inv.invoiceNumber.toLowerCase().includes(term) ||
            new Date(inv.invoiceDate).toLocaleDateString().includes(term)
        );
        setFilteredInvoices(filtered);
    }, [searchTerm, invoices]);

    // Handle invoice selection
    const handleSelectInvoice = (invoice: InvoiceOption) => {
        const isSelected = selectedInvoiceIds.includes(invoice._id);
        let newSelectedIds: string[] = [];

        if (multiple) {
            if (isSelected) {
                newSelectedIds = selectedInvoiceIds.filter(id => id !== invoice._id);
            } else {
                newSelectedIds = [...selectedInvoiceIds, invoice._id];
            }
        } else {
            newSelectedIds = isSelected ? [] : [invoice._id];
        }

        // Find the selected invoice objects
        const selectedInvoicesList = invoices.filter(inv =>
            newSelectedIds.includes(inv._id)
        );

        const totalAmount = selectedInvoicesList.reduce(
            (sum, inv) => sum + (inv.balanceAmount || 0), 0
        );

        onSelectionChange(selectedInvoicesList, totalAmount);
    };

    // Handle select all
    const handleSelectAll = () => {
        const totalAmount = filteredInvoices.reduce(
            (sum, inv) => sum + (inv.balanceAmount || 0), 0
        );
        onSelectionChange(filteredInvoices, totalAmount);
    };

    // Handle clear all
    const handleClearAll = () => {
        onSelectionChange([], 0);
    };

    // Check if all filtered are selected
    const isAllSelected = filteredInvoices.length > 0 &&
        filteredInvoices.every(inv => selectedInvoiceIds.includes(inv._id));

    // Format currency
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    // Format date
    const formatDate = (dateString: string) => {
        try {
            return new Date(dateString).toLocaleDateString('en-GB');
        } catch {
            return 'Invalid Date';
        }
    };

    if (!partyId && !vendorId) {
        return (
            <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                Select a vendor or party to view invoices
            </Alert>
        );
    }

    if (loading) {
        return (
            <Card sx={{ p: compact ? 2 : 3, textAlign: 'center' }}>
                <CircularProgress size={compact ? 24 : 32} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Loading invoices...
                </Typography>
            </Card>
        );
    }

    const emptyMessage = " No unpaid invoices found for this party.";

    return (
        <Card sx={{ p: compact ? 1.5 : 2 }}>
            {/* Header */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant={compact ? 'subtitle1' : 'h6'}>
                    {title}
                </Typography>
            </Box>

            {/* Search */}
            {invoices.length > 0 && (
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by invoice number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
            )}

            {/* Selection Actions */}
            {filteredInvoices.length > 0 && (
                <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={handleSelectAll}
                        disabled={isAllSelected}
                    >
                        Select All
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={handleClearAll}
                        disabled={selectedInvoiceIds.length === 0}
                    >
                        Clear All
                    </Button>
                </Box>
            )}

            {/* Invoices Table */}
            {filteredInvoices.length === 0 ? (
                <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                    {emptyMessage}
                </Alert>
            ) : (
                <>
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                        <Table stickyHeader size={compact ? 'small' : 'medium'}>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox" width={40}>
                                        <Checkbox
                                            checked={isAllSelected}
                                            indeterminate={selectedInvoiceIds.length > 0 && !isAllSelected}
                                            onChange={handleSelectAll}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Invoice No.</TableCell>
                                    <TableCell align="right">Total (₹)</TableCell>
                                    <TableCell align="right">Balance (₹)</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredInvoices.map((invoice) => {
                                    const isSelected = selectedInvoiceIds.includes(invoice._id);

                                    return (
                                        <TableRow
                                            key={invoice._id}
                                            hover
                                            onClick={() => handleSelectInvoice(invoice)}
                                            sx={{
                                                cursor: 'pointer',
                                                bgcolor: isSelected ? 'action.selected' : 'inherit'
                                            }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={isSelected}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatDate(invoice.invoiceDate)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {invoice.invoiceNumber || invoice.purchaseNumber}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography variant="body2">
                                                    {formatCurrency(invoice.totalAmount || 0)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                    color="warning.main"
                                                >
                                                    {formatCurrency(invoice.balanceAmount || 0)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Summary */}
                    {selectedInvoiceIds.length > 0 && (
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'background.default', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="body2">
                                    <strong>{selectedInvoiceIds.length}</strong> invoice(s) selected
                                </Typography>
                                <Typography variant="subtitle2" color="primary.main">
                                    Total: {formatCurrency(
                                        filteredInvoices
                                            .filter(inv => selectedInvoiceIds.includes(inv._id))
                                            .reduce((sum, inv) => sum + (inv.balanceAmount || 0), 0)
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </>
            )}
        </Card>
    );
};

export default InvoiceSelector;